import * as Sentry from "@sentry/react"

export const localStore = storageFactory(() => localStorage)
export const sessionStore = storageFactory(() => sessionStorage)

export class StoreError extends Error {
  constructor() {
    super(
      "DOMException thrown (e.g. SecurityError/QuotaExceededError). Possible causes: private/incognito mode, site data blocked, CSP/extension interference, or storage quota."
    )
    this.name = "StorageUnavailableError"
    Object.setPrototypeOf(this, StoreError.prototype) // https://stackoverflow.com/a/41429145/6444
  }
}

function storageFactory(getStorage: () => Storage): Storage {
  function checkForSupport() {
    try {
      const testKey = "¯(ツ)/¯"
      getStorage().setItem(testKey, testKey)
      getStorage().removeItem(testKey)
      return true
    } catch (e) {
      console.log(e)
      Sentry.captureException(e)
      throw new StoreError()
    }
  }

  function clear(): void {
    if (checkForSupport()) {
      getStorage().clear()
    }
  }

  function getItem(name: string): string | null {
    if (checkForSupport()) {
      return getStorage().getItem(name)
    }
    return null
  }

  function key(index: number): string | null {
    if (checkForSupport()) {
      return getStorage().key(index)
    }
    return null
  }

  function removeItem(name: string): void {
    if (checkForSupport()) {
      getStorage().removeItem(name)
    }
  }

  function setItem(name: string, value: string): void {
    if (checkForSupport()) {
      getStorage().setItem(name, value)
    }
  }

  function length(): number {
    if (checkForSupport()) {
      return getStorage().length
    }
    return 0
  }

  return {
    getItem,
    setItem,
    removeItem,
    clear,
    key,
    get length() {
      return length()
    },
  }
}
